import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkActions = ({ registries, onBulkSync, onBulkConfigure }) => {
  const [selectedRegistries, setSelectedRegistries] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRegistries(registries?.map(r => r?.id));
    } else {
      setSelectedRegistries([]);
    }
  };

  const handleSelectRegistry = (registryId, checked) => {
    if (checked) {
      setSelectedRegistries(prev => [...prev, registryId]);
    } else {
      setSelectedRegistries(prev => prev?.filter(id => id !== registryId));
    }
  };

  const handleBulkSync = async () => {
    if (selectedRegistries?.length === 0) return;
    
    setIsProcessing(true);
    setProgress(0);
    
    try {
      await onBulkSync(selectedRegistries, (completed, total) => {
        setProgress((completed / total) * 100);
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const connectedRegistries = registries?.filter(r => r?.status === 'connected');
  const isAllSelected = selectedRegistries?.length === registries?.length;
  const isIndeterminate = selectedRegistries?.length > 0 && selectedRegistries?.length < registries?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Bulk Actions</h3>
          <p className="text-sm text-muted-foreground">Manage multiple registries simultaneously</p>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            label="Select All"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={(e) => handleSelectAll(e?.target?.checked)}
          />
        </div>
      </div>
      {/* Registry Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {registries?.map((registry) => (
          <div key={registry?.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
            <Checkbox
              checked={selectedRegistries?.includes(registry?.id)}
              onChange={(e) => handleSelectRegistry(registry?.id, e?.target?.checked)}
            />
            <div className="flex items-center space-x-2 flex-1">
              <Icon name={registry?.icon} size={20} className="text-primary" />
              <div>
                <div className="text-sm font-medium text-card-foreground">{registry?.name}</div>
                <div className={`text-xs ${
                  registry?.status === 'connected' ? 'text-success' : 'text-muted-foreground'
                }`}>
                  {registry?.status}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Progress Bar */}
      {isProcessing && (
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-card-foreground">Syncing registries...</span>
            <span className="text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center space-x-4">
        <Button
          variant="default"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={handleBulkSync}
          disabled={selectedRegistries?.length === 0 || isProcessing}
          loading={isProcessing}
        >
          Sync Selected ({selectedRegistries?.length})
        </Button>
        <Button
          variant="outline"
          iconName="Settings"
          iconPosition="left"
          onClick={() => onBulkConfigure(selectedRegistries)}
          disabled={selectedRegistries?.length === 0 || isProcessing}
        >
          Configure Selected
        </Button>
        <div className="text-sm text-muted-foreground">
          {connectedRegistries?.length} of {registries?.length} registries connected
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-success">{connectedRegistries?.length}</div>
          <div className="text-xs text-muted-foreground">Connected</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-warning">
            {registries?.filter(r => r?.status === 'syncing')?.length}
          </div>
          <div className="text-xs text-muted-foreground">Syncing</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-error">
            {registries?.filter(r => r?.status === 'error')?.length}
          </div>
          <div className="text-xs text-muted-foreground">Errors</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-muted-foreground">
            {registries?.filter(r => r?.status === 'disconnected')?.length}
          </div>
          <div className="text-xs text-muted-foreground">Offline</div>
        </div>
      </div>
    </div>
  );
};

export default BulkActions;